import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Paper,
  Switch,
  FormControlLabel,
  CircularProgress,
  useTheme,
  useMediaQuery
} from '@mui/material';
import EnhancedSkeleton from '../Summary/EnhancedSkeleton';
import { timeToSec } from '../../utils';

const TranscriptSection = ({
  scriptArr,
  activeIdx,
  autoScroll,
  setAutoScroll,
  scriptRefs,
  playerRef,
  loading,
  transcript,           // 원본 자막
  translatedTranscript, // 번역된 자막
  showTranslated = true, // 번역문/원문 토글 상태 (디폴트: 번역문)
  onToggleTranslation   // 번역문/원문 토글 콜백
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // 자막 라인 클릭 시 플레이어 이동
  const handleLineClick = (time) => {
    if (playerRef && playerRef.current && typeof playerRef.current.seekTo === 'function') {
      playerRef.current.seekTo(timeToSec(time), true);
    }
  };

  // 시간 문자열에서 [[00:00]] -> [00:00]으로 변환
  const cleanTime = (time) => {
    if (!time) return '';
    return time.replace(/\[\[(\d{2}:\d{2})\]\]/, '[$1]').replace(/\[\[(\d{2}:\d{2})\]\]/g, '[$1]');
  };

  // 자동 스크롤 처리 - 하이라이트가 바뀔 때마다 항상 스크롤
  useEffect(() => {
    if (!autoScroll || activeIdx === -1 || !scriptRefs.current[activeIdx]) return;

    const activeElement = scriptRefs.current[activeIdx];
    
    if (activeElement) {
      // 하이라이트가 바뀔 때마다 항상 해당 구간으로 스크롤
      activeElement.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }
  }, [activeIdx, autoScroll]);

  return (
    <Paper
      elevation={0}
      sx={{
        p: isMobile ? 1.5 : 2.5,
        pb: isMobile ? 1.5 : 3,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#fff',
        borderRadius: 1.5,
        boxShadow: '0 0 20px rgba(0,0,0,0.05)',
        overflow: 'hidden'
      }}
    >
      {/* 반응형 헤더: 넓을 때는 한 줄, 좁을 때는 두 줄 */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: translatedTranscript ? 'column' : 'row', md: 'row' },
        justifyContent: { xs: 'flex-start', sm: translatedTranscript ? 'flex-start' : 'space-between', md: 'space-between' },
        alignItems: { xs: 'stretch', sm: translatedTranscript ? 'stretch' : 'center', md: 'center' },
        gap: { xs: 1, sm: translatedTranscript ? 1 : 2, md: 2 },
        mb: isMobile ? 1 : 2,
        px: isMobile ? 0.3 : 1
      }}>
        {/* 첫 번째 줄 또는 상단: 제목과 번역문/원문 토글 */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center',
          gap: 2,
          flex: { xs: 'none', sm: translatedTranscript ? 'none' : 1, md: 1 }
        }}>
          <Typography 
            variant="h6" 
            sx={{ 
              fontWeight: 600,
              color: '#1976d2'
            }}
          >
            {t('analyze.transcript')}
          </Typography>
          
          {/* 번역문-원문 선택기 (translatedTranscript가 있을 때만 표시) */}
          {translatedTranscript && (
            <Box
              sx={{
                display: 'inline-flex',
                backgroundColor: 'rgba(25, 118, 210, 0.08)',
                borderRadius: '16px',
                padding: '2px',
                position: 'relative'
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  position: 'relative',
                  zIndex: 1
                }}
              >
                <Box
                  onClick={() => onToggleTranslation && onToggleTranslation(true)}
                  sx={{
                    px: 1.5,
                    py: 0.5,
                    borderRadius: '14px',
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    color: showTranslated ? '#fff' : '#1976d2',
                    backgroundColor: showTranslated ? '#1976d2' : 'transparent',
                    '&:hover': {
                      backgroundColor: showTranslated ? '#1565c0' : 'rgba(25, 118, 210, 0.12)'
                    }
                  }}
                >
                  {t('analyze.translated')}
                </Box>
                <Box
                  onClick={() => onToggleTranslation && onToggleTranslation(false)}
                  sx={{
                    px: 1.5,
                    py: 0.5,
                    borderRadius: '14px',
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    color: !showTranslated ? '#fff' : '#1976d2',
                    backgroundColor: !showTranslated ? '#1976d2' : 'transparent',
                    '&:hover': {
                      backgroundColor: !showTranslated ? '#1565c0' : 'rgba(25, 118, 210, 0.12)'
                    }
                  }}
                >
                  {t('analyze.original')}
                </Box>
              </Box>
            </Box>
          )}
        </Box>
        
        {/* 두 번째 줄 또는 우측: 자동 스크롤 토글 */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'flex-end',
          alignItems: 'center'
        }}>
          <FormControlLabel
            control={
              <Switch
                checked={autoScroll}
                onChange={(e) => setAutoScroll(e.target.checked)}
                color="primary"
              />
            }
            label={t('analyze.autoScroll')}
          />
        </Box>
      </Box>
      <Box
        className="scrollable-content"
        sx={{
          flex: 1,
          overflowY: 'auto',
          minHeight: 0,
          px: isMobile ? 0.3 : 1,
          pb: isMobile ? 0.5 : 1.5,
          position: 'relative',
          scrollbarWidth: 'thin',
          scrollbarColor: 'rgba(25, 118, 210, 0.10) transparent',
          '&::-webkit-scrollbar': {
            width: '7px',
            background: 'transparent',
          },
          '&:not(:active):not(:focus):not(:hover)::-webkit-scrollbar-thumb': {
            background: 'transparent',
          },
          '&:hover::-webkit-scrollbar-thumb': {
            background: 'rgba(25, 118, 210, 0.10)',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(25, 118, 210, 0.10)',
            borderRadius: '4px',
            minHeight: '40px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: 'rgba(25, 118, 210, 0.18)',
            },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
        }}
      >
        {loading && (
          <Box sx={{ px: isMobile ? 0.3 : 1 }}>
            {/* 자막 로딩 스켈레톤 */}
            {Array.from(new Array(8)).map((_, index) => (
              <Box key={index} sx={{ mb: isMobile ? 0.8 : 1.2, p: isMobile ? 0.8 : 1.2, pl: 0, px: 0 }}>
                <EnhancedSkeleton 
                  variant="text" 
                  height={20} 
                  width={index === 7 ? '60%' : '100%'}
                  animationType="shimmer"
                  sx={{ 
                    borderRadius: 1,
                    bgcolor: 'rgba(25, 118, 210, 0.08)'
                  }}
                />
              </Box>
            ))}
          </Box>
        )}
        {!loading && scriptArr && scriptArr.length > 0 && scriptArr.map((line, idx) => (
          <Box
            key={idx}
            ref={el => scriptRefs.current[idx] = el}
            onClick={() => handleLineClick(line.time)}
            sx={{
              p: isMobile ? 0.8 : 1.2,
              pl: 0,
              px: 0,
              mb: isMobile ? 0.2 : 0.4,
              borderRadius: 1,
              transition: 'all 0.2s ease',
              cursor: 'pointer',
              bgcolor: activeIdx === idx ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
              border: activeIdx === idx 
                ? '1px solid rgba(25, 118, 210, 0.2)'
                : '1px solid transparent',
              '&:hover': {
                bgcolor: activeIdx === idx 
                  ? 'rgba(25, 118, 210, 0.12)'
                  : 'rgba(0, 0, 0, 0.04)',
              }
            }}
          >
            <Typography
              sx={{
                fontSize: 14,
                color: activeIdx === idx ? '#1976d2' : '#666',
                fontWeight: activeIdx === idx ? 500 : 400,
                lineHeight: 1.6
              }}
            >
              {[line.time ? `${cleanTime(line.time)} ` : '', line.text].join('')}
            </Typography>
          </Box>
        ))}
      </Box>
    </Paper>
  );
};

export default TranscriptSection; 